import { useState } from 'react';
import type { NumberFormatValues } from 'react-number-format';
import { useDispatch } from 'react-redux';

import Field from '@opendoor/cinderblocks/growth/novo/inputs/Field';
import {
  NumericInput,
  NumericInputProps,
} from '@opendoor/cinderblocks/growth/novo/inputs/NumericFormat';

import { trackAnswerChange } from 'src/components/fields/trackAnswerChange';
import { getErrorMessage } from 'src/components/flowPage/twoColumn/ErrorMessages';
import { FlowQuestion, NumberQuestion } from 'src/lib/flow/FlowQuestion';
import { checkValidation } from 'src/lib/flow/validation';
import { errorAnswer } from 'src/store/sellerFlow/actions';
import { changeAnswers } from 'src/store/sellerInput';

type NumberInputProps = {
  question: NumberQuestion;
  value?: string;
  showValidation?: boolean;
};

const NumberInput: React.FC<NumberInputProps> = ({ question, value, showValidation }) => {
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { key, placeholder, label, suffix, helperText, customValidatorErrorMessage } = question;

  // on answer change, only validate the maximum
  const onValueChange = (values: NumberFormatValues) => {
    let answer = values?.floatValue ?? null;

    let errorMessage: string | undefined;
    if (typeof answer === 'number' && !Number.isNaN(answer)) {
      const result = checkValidation(question as FlowQuestion, answer);
      if (!result.isValid && (showValidation || result.type == 'too_large')) {
        errorMessage = getErrorMessage(result);
      }
    } else {
      answer = null;
    }

    // error message will be undefined when none or a message we should display
    // in both cases we want to call these two functions
    setErrorMessage(errorMessage ?? undefined);
    dispatch(errorAnswer(key, !!errorMessage, errorMessage ?? undefined));

    dispatch(changeAnswers([{ key, answer }]));
    trackAnswerChange(key, answer ?? undefined);
  };

  let validationErrorMessage: string | undefined;
  if (showValidation) {
    const answer = value !== null && value !== undefined ? parseInt(value, 10) : null;
    const result = checkValidation(question as FlowQuestion, answer);
    validationErrorMessage =
      customValidatorErrorMessage || (!result.isValid ? getErrorMessage(result) : undefined);
  }

  // `value` - is our initialValue, but for numbers we want to allow the user to delete a prefill
  // this.state.shouldOverridePrefillWithUndefined ? undefined : answer;
  // we may need to still check that the user has not manually cleared out an input
  // so that we do not show the answer prefill
  //
  return (
    <Field<NumericInputProps>
      label={label}
      helperText={helperText}
      errorText={errorMessage || validationErrorMessage}
      InputComponent={NumericInput}
      input={{
        id: `integer-${key}`,
        error: !!errorMessage || !!validationErrorMessage,
        name: key,
        value: value ? value.toString() : undefined,
        thousandSeparator: ',',
        formattedSuffix: suffix,
        onValueChange,
        type: 'text',
        placeholder,
      }}
    />
  );
};

export default NumberInput;
